import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  Paper,
  Divider,
  Chip,
  Container
} from '@mui/material';
import parse from 'html-react-parser';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useFetchThisAssignmentMCQResultQuery,
  useFetchThisSessionAssignmentQuery,
  useListAllCourseQuery
} from 'redux/reducers/user/dashboard/tms-course';
import MCQResultModal from './MCQResultModal';
import { BiCheckCircle, BiSave } from 'react-icons/bi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`course-tabpanel-${index}`}
      aria-labelledby={`course-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface IAssignment {
  moduleID: any;
  sessionID: number;
  sessionName: string;
  Assignments: [
    {
      assignment_title: string;
      tms_assignment_id: string;
      attempt: boolean;
    }
  ];
}

const ContentTab: React.FC<IAssignment> = ({ sessionID, moduleID, sessionName, Assignments }) => {
  const { id } = useParams();
  const [value, setValue] = useState('1');
  const [activeAssignmentID, setActiveAssignmentID] = React.useState<any>();
  const [getThisAssignmentResult, setGetThisAssignmentResult] = React.useState<any>();
  const [openForgetPasswordModal, setOpenForgetPasswordModal] = React.useState(false);

  const { data: DataAssignment } = useFetchThisSessionAssignmentQuery(activeAssignmentID);
  const { data: MCQResult } = useFetchThisAssignmentMCQResultQuery(getThisAssignmentResult);
  const { data: allCourse } = useListAllCourseQuery();

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const detail = allCourse?.filter((item) => item.id == parseInt(id as string));
  const navigate = useNavigate();

  React.useEffect(() => {
    if (DataAssignment) {
      setActiveAssignmentID(DataAssignment?.data?.results[0]?.tms_assignment_id);
    }
  }, [DataAssignment]);

  const userCourseViewRedirect = (assignmentId: any) => {
    const mcqpage = '/course/enroll/view/moduleid=:mid/:sessionID/:id/mcq';
    navigate(
      mcqpage
        .replace(':mid', moduleID)
        .replace(':id', assignmentId)
        .replace(':sessionID', sessionID.toString())
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
      <MCQResultModal
        MCQResult={MCQResult}
        handleClose={() => setOpenForgetPasswordModal(false)}
        openForgetPasswordModal={openForgetPasswordModal}
      />

      <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {/* Responsive Tabs Header */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fcfcfc' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 64,
                fontSize: '1rem',
              },
            }}
          >
            <Tab label="Course Overview" value="1" />
            <Tab label={`Assignments (${Assignments?.length || 0})`} value="3" />
          </Tabs>
        </Box>

        {/* Tab Content Area */}
        <Box sx={{ minHeight: '400px', p: { xs: 1, sm: 3 } }}>
          
          {/* OVERVIEW PANEL */}
          <CustomTabPanel value={value} index="1">
            <Typography variant="h5" fontWeight={700} gutterBottom color="primary.main">
              About this course
            </Typography>
            <Divider sx={{ mb: 3, width: '60px', height: '4px', bgcolor: 'primary.main', borderRadius: 1 }} />
            <Box sx={{ 
              lineHeight: 1.7, 
              color: 'text.secondary',
              '& p': { mb: 2 } 
            }}>
              {detail?.map((item) => (
                <div key={item.id}>{parse(item.description)}</div>
              ))}
            </Box>
          </CustomTabPanel>

          {/* ASSIGNMENTS PANEL */}
          <CustomTabPanel value={value} index="3">
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight={700}>
                {sessionName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please complete all assignments to proceed.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {Assignments?.length > 0 ? (
                Assignments?.map((assignment, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '16px',
                        height: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0px 10px 20px rgba(0,0,0,0.05)',
                          borderColor: 'primary.light',
                        }
                      }}
                    >
                      <CardActionArea 
                        sx={{ height: '100%' }}
                        onClick={() => {
                          if (assignment?.attempt === false) {
                            userCourseViewRedirect(assignment?.tms_assignment_id);
                          } else {
                            setGetThisAssignmentResult(assignment?.tms_assignment_id);
                            setOpenForgetPasswordModal(true);
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                            <Box sx={{ 
                              p: 1, 
                              borderRadius: '10px', 
                              bgcolor: assignment.attempt ? 'success.lighter' : 'primary.lighter',
                              color: assignment.attempt ? 'success.main' : 'primary.main',
                              display: 'flex'
                            }}>
                              {assignment.attempt ? <BiCheckCircle /> : <BiSave />}
                            </Box>
                            <Chip 
                              label={assignment.attempt ? "Completed" : "Pending"} 
                              size="small"
                              color={assignment.attempt ? "success" : "warning"}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          </Stack>

                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, minHeight: '3rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {assignment?.assignment_title}
                          </Typography>

                          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                          <Typography variant="button" color="primary" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                            {assignment?.attempt === false ? 'START ASSIGNMENT →' : 'VIEW SCORE →'}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 10, bgcolor: '#f9f9f9', borderRadius: 4 }}>
                    <BiSave  />
                    <Typography color="text.secondary">No assignments available for this session.</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CustomTabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default ContentTab;